import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { Flex, Select, Space } from '@procurify/ui'
import { DateTime, Interval } from 'luxon'
import { useSelector } from 'react-redux'

import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  useFormatBudget,
  useCalculateBudgetSubtotals,
  type FormattedBudget,
} from '@webapp/hooks/useFormatBudget'
import { useFormatBudgetPeriod } from '@webapp/hooks/useFormatBudgetPeriod'
import { useFormatISOStringToDate } from '@webapp/hooks/useFormatISOStringToDate'
import { type BudgetBreakdownBar } from '@webapp/hooks/useGetBudgetBreakdown'
import {
  type BudgetCategoryDetails,
  type BudgetGroupBudget,
} from '@webapp/models'
import messages from './messages'
import { BudgetCategoryCard } from '../BudgetCategoryCard'
import { BudgetCategoryDetailsCardStyled } from './styles/BudgetCategoryDetailsCardStyled'

export interface IBudgetCategoryDetailsCardProps {
  budgetCategory: BudgetCategoryDetails
  headquartersTimezone: TimezonesEnum
  onEmptyStateButtonClick: () => void
  renderMenuButton?: () => ReactNode
}

interface IOldBudgetCategoryDetailsCardProps {
  budgetCategory: BudgetCategoryDetails
  currentBudget: FormattedBudget
  showOldBudgetBreakdown: BudgetBreakdownBar[]
  showEmptyState: boolean
  budgetPeriodOptions: any
  selectedBudgetPeriod: any
  showOldBudgetDetails: boolean
  handleOnBudgetPeriodSelect: (option: any) => void
  onEmptyStateButtonClick: () => void
  renderMenuButton?: () => ReactNode
}

const OldBudgetCategoryDetailsCard = ({
  budgetCategory,
  currentBudget,
  showOldBudgetBreakdown,
  showEmptyState,
  showOldBudgetDetails,
  budgetPeriodOptions,
  selectedBudgetPeriod,
  handleOnBudgetPeriodSelect,
  onEmptyStateButtonClick,
  renderMenuButton,
}: IOldBudgetCategoryDetailsCardProps) => {
  const intl = useProcurifyIntl()
  const { enable_flexible_spend_tracking: isFlexibleSpendTrackingEnabled } =
    useSelector(makeSelectFeatureSwitches())

  return (
    <BudgetCategoryDetailsCardStyled>
      <BudgetCategoryCard>
        <BudgetCategoryCard.Header
          name={budgetCategory.name}
          startDate={currentBudget?.startDateTime}
          endDate={currentBudget?.endDateTime}
          budgetBreakdown={showOldBudgetBreakdown}
          renderMenuButton={renderMenuButton}
          budgetSelect={
            budgetPeriodOptions?.length ? (
              <Flex.Item width={1 / 2}>
                <Select
                  value={selectedBudgetPeriod}
                  options={budgetPeriodOptions}
                  getOptionValue={(option) => option.value}
                  onChange={handleOnBudgetPeriodSelect}
                />
              </Flex.Item>
            ) : null
          }
        />
        {showOldBudgetDetails && (
          <>
            <BudgetCategoryCard.DetailsBreakdown
              ariaLabel={intl.formatMessage(
                isFlexibleSpendTrackingEnabled
                  ? messages.categoryBreakdownTableLabel
                  : messages.oldCategoryBreakdownTableLabel
              )}
            >
              {currentBudget.budget ? (
                <BudgetCategoryCard.DetailsBreakdown.Row
                  name={intl.formatMessage(messages.budget)}
                  amount={currentBudget.budget}
                />
              ) : null}
              <BudgetCategoryCard.DetailsBreakdown.Row
                name={intl.formatMessage(messages.currentSpend)}
                amount={currentBudget.currentSpend}
              />
              {Boolean(currentBudget.variance) &&
                Boolean(currentBudget.variancePercentage) && (
                  <BudgetCategoryCard.DetailsBreakdown.VarianceRow
                    variance={currentBudget.variance}
                    variancePercentage={currentBudget.variancePercentage}
                    danger={currentBudget.isOverBudget}
                  />
                )}
            </BudgetCategoryCard.DetailsBreakdown>
            <BudgetCategoryCard.GranularSpend
              ariaLabel={intl.formatMessage(messages.granularSpendTableLabel)}
            >
              <BudgetCategoryCard.GranularSpend.Row
                name={intl.formatMessage(messages.approvedSpend)}
                amount={currentBudget.approved}
                iconColor={
                  currentBudget.budgetBreakdown.length
                    ? currentBudget.budgetBreakdown[0]?.color
                    : null
                }
              />
              <BudgetCategoryCard.GranularSpend.Row
                name={intl.formatMessage(messages.purchasedSpend)}
                amount={currentBudget.purchased}
                iconColor={
                  currentBudget.budgetBreakdown.length
                    ? currentBudget.budgetBreakdown[1]?.color
                    : null
                }
              />
              <BudgetCategoryCard.GranularSpend.Row
                name={intl.formatMessage(messages.billedSpend)}
                amount={currentBudget.invoiced}
                iconColor={
                  currentBudget.budgetBreakdown.length
                    ? currentBudget.budgetBreakdown[2]?.color
                    : null
                }
              />
              <BudgetCategoryCard.GranularSpend.Row
                name={intl.formatMessage(messages.pendingRequests)}
                amount={currentBudget.otherPendingRequests}
                iconColor={
                  currentBudget.budgetBreakdown.length
                    ? currentBudget.budgetBreakdown[3]?.color
                    : null
                }
              />
            </BudgetCategoryCard.GranularSpend>
          </>
        )}
        {showEmptyState && (
          <BudgetCategoryCard.EmptyState
            onClickEditCategoryButton={onEmptyStateButtonClick}
          />
        )}
      </BudgetCategoryCard>
    </BudgetCategoryDetailsCardStyled>
  )
}

export const BudgetCategoryDetailsCard = ({
  budgetCategory,
  headquartersTimezone,
  onEmptyStateButtonClick,
  renderMenuButton = () => undefined,
}: IBudgetCategoryDetailsCardProps) => {
  const intl = useProcurifyIntl()
  const { enable_flexible_spend_tracking: isFlexibleSpendTrackingEnabled } =
    useSelector(makeSelectFeatureSwitches())
  const todayInDateTime = DateTime.now()

  const [selectedBudgetPeriod, setSelectedBudgetPeriod] = useState(null)
  const [budgetPeriodOptions, setBudgetPeriodOptions] = useState(null)
  const [currentBudgetGroup, setCurrentBudgetGroup] = useState(null)

  const formatISOStringToDate = useFormatISOStringToDate()
  const formatBudgetPeriod = useFormatBudgetPeriod()

  const { budget: currentBudget, setBudgetToFormat } = useFormatBudget()
  const [selectedBudget, setSelectedBudget] = useState<BudgetGroupBudget>()
  const calculateBudgetSubtotals = useCalculateBudgetSubtotals()
  const subtotals = calculateBudgetSubtotals(selectedBudget)

  const setBudget = (budget: BudgetGroupBudget | null) => {
    setSelectedBudget(budget)
    setBudgetToFormat(budget)
  }

  useEffect(() => {
    const { budget_groups } = budgetCategory

    const currBudgetGroup = budget_groups.find((group) => {
      const { start_datetime, end_datetime } = group

      const startDate = formatISOStringToDate(
        start_datetime,
        headquartersTimezone
      )
      const endDate = formatISOStringToDate(end_datetime, headquartersTimezone)
      const budgetGroupDateTimeInterval = Interval.fromDateTimes(
        startDate,
        endDate
      )

      if (budgetGroupDateTimeInterval.contains(todayInDateTime)) {
        setCurrentBudgetGroup(group)
        return group
      }
    })

    const { budgets: budgets = [], interval } = currBudgetGroup || {}

    const budgetOptions = []

    if (interval !== null) {
      budgets
        .sort((a, b) => {
          const startDateA = DateTime.fromISO(a.start_datetime, {
            zone: headquartersTimezone,
          }).toMillis()

          const startDateB = DateTime.fromISO(b.start_datetime, {
            zone: headquartersTimezone,
          }).toMillis()

          return startDateA - startDateB
        })
        .map((budget) => {
          const { start_datetime, end_datetime, id } = budget

          const startDate = formatISOStringToDate(
            start_datetime,
            headquartersTimezone
          )
          const endDate = formatISOStringToDate(
            end_datetime,
            headquartersTimezone
          )

          const budgetDateTimeInterval = Interval.fromDateTimes(
            startDate,
            endDate
          )

          let label = formatBudgetPeriod(
            start_datetime,
            end_datetime,
            interval,
            headquartersTimezone
          )

          if (budgetDateTimeInterval.contains(todayInDateTime)) {
            label = label.concat(
              `${intl.formatMessage(messages.currentBudgetPeriodOptionSuffix)}`
            )
            setSelectedBudgetPeriod({
              label,
              value: id,
            })
            setBudget(budget)
          }

          budgetOptions.push({
            label,
            value: id,
          })
        })

      setBudgetPeriodOptions(budgetOptions)
    } else {
      budgets?.find((budget) => {
        const { start_datetime, end_datetime } = budget

        const startDate = formatISOStringToDate(
          start_datetime,
          headquartersTimezone
        )
        const endDate = formatISOStringToDate(
          end_datetime,
          headquartersTimezone
        )

        const budgetDateTimeInterval = Interval.fromDateTimes(
          startDate,
          endDate
        )

        if (budgetDateTimeInterval.contains(todayInDateTime)) setBudget(budget)
      })
    }

    return () => {
      setBudget(null)
      setSelectedBudgetPeriod(null)
      setBudgetPeriodOptions(null)
      setCurrentBudgetGroup(null)
    }
  }, [budgetCategory])

  const handleOnBudgetPeriodSelect = (option) => {
    setSelectedBudgetPeriod(option)

    const { budgets } = currentBudgetGroup

    const currBudget = budgets
      ? budgets.find((budget) => {
          const { id } = budget

          if (id === option.value) return budget
        })
      : null

    setBudget(currBudget)
  }

  const showOldBudgetDetails = Boolean(
    !isFlexibleSpendTrackingEnabled &&
    currentBudget?.startDateTime &&
    currentBudget?.endDateTime
  )
  const showOldBudgetBreakdown =
    !isFlexibleSpendTrackingEnabled && currentBudget?.budget
      ? currentBudget?.budgetBreakdown
      : undefined
  const showNewBudgetDetails = Boolean(
    isFlexibleSpendTrackingEnabled && selectedBudget
  )
  const showEmptyState = !showOldBudgetDetails && !showNewBudgetDetails

  if (showNewBudgetDetails) {
    return (
      <BudgetCategoryDetailsCardStyled>
        <BudgetCategoryCard>
          <Space size='medium'>
            <BudgetCategoryCard.Header
              name={budgetCategory.name}
              startDate={currentBudget?.startDateTime}
              endDate={currentBudget?.endDateTime}
              budgetBreakdown={showOldBudgetBreakdown}
              renderMenuButton={renderMenuButton}
              budgetSelect={
                budgetPeriodOptions && budgetPeriodOptions?.length ? (
                  <Space.Item width={1 / 2}>
                    <Select
                      value={selectedBudgetPeriod}
                      options={budgetPeriodOptions}
                      getOptionValue={(option) => option.value}
                      onChange={handleOnBudgetPeriodSelect}
                    />
                  </Space.Item>
                ) : null
              }
            />
            {subtotals.budgetType === 'fixed' && (
              <BudgetCategoryCard.ProgressBar
                totalSpend={subtotals.spendTotal}
                committedSpend={subtotals.committed}
                spendRequest={subtotals.spendRequest}
                pendingRequests={subtotals.pending}
                remainingAfterPendingRequests={subtotals.remainingMinusPending}
                isOverBudget={subtotals.isOverBudget}
                largeTooltip
              />
            )}
            <BudgetCategoryCard.Subtotals
              totalBudget={subtotals.total}
              totalSpend={subtotals.spendTotal}
              committedSpend={subtotals.committed}
              approvedSpend={subtotals.approved}
              purchasedSpend={subtotals.purchased}
              billedSpend={subtotals.billed}
              receivedSpend={subtotals.received}
              spendRequest={subtotals.spendRequest}
              remaining={subtotals.remaining}
              pendingRequests={subtotals.pending}
              remainingAfterPendingRequests={subtotals.remainingMinusPending}
              isOverBudget={subtotals.isOverBudget}
              shouldRenderAccordion
              shouldRenderPercentagesFirst={subtotals.budgetType === 'flexible'}
            />
          </Space>
        </BudgetCategoryCard>
      </BudgetCategoryDetailsCardStyled>
    )
  }

  return (
    <OldBudgetCategoryDetailsCard
      budgetCategory={budgetCategory}
      currentBudget={currentBudget}
      showOldBudgetBreakdown={showOldBudgetBreakdown}
      showEmptyState={showEmptyState}
      showOldBudgetDetails={showOldBudgetDetails}
      budgetPeriodOptions={budgetPeriodOptions}
      selectedBudgetPeriod={selectedBudgetPeriod}
      handleOnBudgetPeriodSelect={handleOnBudgetPeriodSelect}
      onEmptyStateButtonClick={onEmptyStateButtonClick}
      renderMenuButton={renderMenuButton}
    />
  )
}

BudgetCategoryDetailsCard.displayName = 'BudgetCategoryDetailsCard'
